/**
 * Whether residents may see a cycle's bill, and what to send to say so.
 *
 * Two documents can stand behind one allocation bill, and they are shared by different means:
 *
 * - A file uploaded here lives in the file service, and the bill carries its `fileId` (or the
 *   `redactedFileId` of a copy with the account numbers taken out).
 * - A bill allocated from the Bills page carries neither. Its document lives in bill-automation and
 *   residents reach it through the bill's `sourceBillId` instead (LB-5724).
 *
 * The second kind had no way to be shared at all: the control was drawn only when there was a
 * `fileId`, and the request builder only filled a payload in that same branch, so drawing it without
 * fixing the builder would have produced a checkbox that did nothing (LB-5875).
 */

/** What the checkbox shows. */
export function isBillShared() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    bill = _ref.bill,
    tempFileData = _ref.tempFileData,
    isForRecoveredBill = _ref.isForRecoveredBill;
  // `tempFileData` is the draft of an upload in progress. A recovered bill has no upload, so nothing
  // ever puts anything there, and the bill itself is the only record of whether it is shared -- as
  // well as the only one that survives closing the modal.
  if (isForRecoveredBill) return (bill === null || bill === void 0 ? void 0 : bill.isShared) === true;
  return !!(tempFileData !== null && tempFileData !== void 0 && tempFileData.isShared);
}

/**
 * The body for `PATCH allocation/api/Bills`, or null when there is nothing to send.
 *
 * @param isShared the value being moved TO, not the one on screen now.
 */
export function toShareRequest() {
  let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    bill = _ref2.bill,
    tempFileData = _ref2.tempFileData,
    isForRecoveredBill = _ref2.isForRecoveredBill,
    fileId = _ref2.fileId,
    isShared = _ref2.isShared;
  if (!(bill !== null && bill !== void 0 && bill.id)) return null;
  if (tempFileData !== null && tempFileData !== void 0 && tempFileData.isDeleted) {
    return {
      billId: bill.id,
      fileId: null,
      isShared: false
    };
  }
  if (isForRecoveredBill) {
    // Deliberately no fileId and no redactedFileId. For a bill carrying a sourceBillId the API reads
    // that shape as a share toggle rather than as a detach, so this cannot drop a redacted copy
    // somebody attached -- which sending `fileId: null` explicitly would.
    return {
      billId: bill.id,
      isShared
    };
  }
  if (!fileId) return null;
  if (tempFileData !== null && tempFileData !== void 0 && tempFileData.isReducted || bill.redactedFileId) {
    return {
      billId: bill.id,
      fileId: bill.fileId || fileId,
      redactedFileId: fileId,
      isShared
    };
  }
  return {
    billId: bill.id,
    fileId,
    isShared
  };
}